import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * @typedef {object} RelatedDestination
 * @property {string} id Stable key for React lists
 * @property {string} label
 * @property {import("react-router-dom").To} to
 */

/**
 * @type {import("react").Context<{
 *   destinations: RelatedDestination[],
 *   setDestinations: (next: RelatedDestination[]) => void,
 * }>}
 */
const RelatedDestinationsContext = createContext({
  destinations: /** @type {RelatedDestination[]} */ ([]),
  setDestinations: /** @type {(next: RelatedDestination[]) => void} */ (() => {}),
});

/**
 * Screen-level related destinations for the top bar (siblings, not nav depth).
 * @param {object} props
 * @param {import("react").ReactNode} props.children
 */
export const RelatedDestinationsProvider = ({ children }) => {
  const [destinations, setDestinations] = useState(/** @type {RelatedDestination[]} */ ([]));
  const value = useMemo(() => ({ destinations, setDestinations }), [destinations]);

  return <RelatedDestinationsContext.Provider value={value}>{children}</RelatedDestinationsContext.Provider>;
};

/**
 * @returns {RelatedDestination[]}
 */
export const useRelatedDestinations = () => useContext(RelatedDestinationsContext).destinations;

/**
 * Register related destinations while this screen is mounted.
 * Clears on unmount. Replaces any previous registration from this hook instance.
 *
 * @param {RelatedDestination[]} destinations
 */
export const useRegisterRelatedDestinations = (destinations) => {
  const { setDestinations } = useContext(RelatedDestinationsContext);
  const signature = useMemo(
    () =>
      JSON.stringify(
        destinations.map((destination) => ({
          id: destination.id,
          label: destination.label,
          to:
            typeof destination.to === "string"
              ? destination.to
              : `${destination.to?.pathname || ""}${destination.to?.search || ""}`,
        }))
      ),
    [destinations]
  );

  useEffect(() => {
    setDestinations(destinations);
    return () => setDestinations([]);
    // signature captures destination identity; destinations object may be new each render
  }, [signature, setDestinations]);
};
